import { useEffect, useMemo, useState } from "react";

import {
  useGetProgressQuery,
  type IRunProgress,
} from "@/services/(company)/payrollRunsApi";
import { socketManager } from "@/services/websocket/socket-manager";
import { getAuthTokensFromLocalStorage } from "@/utils/auth";
import { SOCKET_NAME_SPACES, SOCKET_PAYROLL_EVENTS } from "./socket.enum";

export type IPayrollRunProgress = {
  runId: number;
  processed: number;
  total: number;
  percent: number;
  status: string;
};

export type IPayrollRunItemEvent = {
  itemId: number;
  userId: number;
  status: "COMPLETED" | "FAILED";
  error?: string;
};

export type IPayrollRunCompletedEvent = {
  runId: number;
  totalEmployees: number;
  totalGross: number;
  totalNet: number;
  status: "DRAFT";
};

export type IPayrollProcessStarted = {
  runId: number;
  totalFiles: number;
  cashItemsCount: number;
};

export type IPayrollProcessFileStatus = "GENERATING" | "UPLOADING" | "DONE";

export type IPayrollProcessFile = {
  runId: number;
  fileIndex: number;
  totalFiles: number;
  status: IPayrollProcessFileStatus;
  bankName: string;
  fileName: string;
  fileUrl?: string;
  employeeCount: number;
  totalAmount: number;
  currency?: string;
};

export type IPayrollPaymentFile = {
  fileName: string;
  fileUrl: string;
  bankName: string;
  employeeCount: number;
  totalAmount: number;
  currency?: string;
};

export type IPayrollProcessCompleted = {
  runId: number;
  filesGenerated?: number;
  status: "PROCESSING" | "COMPLETED";
  paymentFiles?: IPayrollPaymentFile[];
};

export type IPayrollProcessFailed = {
  runId: number;
  error: string;
};

export type ISignatureKind = "receipt" | "cash";

export type IPayrollSignatureStarted = {
  runId: number;
  jobId: string;
  kind: ISignatureKind;
  total: number;
};

export type IPayrollSignatureProgress = {
  runId: number;
  jobId: string;
  kind: ISignatureKind;
  itemId: number;
  employeeName?: string;
  current: number;
  total: number;
  status: "sent" | "skipped" | "failed";
  reason?: string;
};

export type IPayrollSignatureCompleted = {
  runId: number;
  jobId: string;
  kind: ISignatureKind;
  sent: number;
  skipped: number;
  failed: number;
};

export type IPayrollSignatureFailed = {
  runId: number;
  jobId: string;
  kind: ISignatureKind;
  error: string;
};

const INITIAL_PROGRESS: IPayrollRunProgress = {
  runId:     0,
  processed: 0,
  total:     0,
  percent:   0,
  status:    "GENERATING",
};

const toProcessedCount = ({
  completed,
  failed,
  skipped,
}: {
  completed: number;
  failed: number;
  skipped: number;
}) => completed + failed + skipped;

const isProgressSnapshotComplete = ({
  total,
  percent,
  completed,
  failed,
  skipped,
  generationFinishedAt,
}: {
  total: number;
  percent: number;
  completed: number;
  failed: number;
  skipped: number;
  generationFinishedAt: string | null;
}) =>
  Boolean(generationFinishedAt) ||
  percent >= 100 ||
  (total > 0 && toProcessedCount({ completed, failed, skipped }) >= total);

export const usePayrollRunSocket = (runId: number | null) => {
  const [progress, setProgress] = useState<IPayrollRunProgress>(INITIAL_PROGRESS);
  const [items, setItems] = useState<IPayrollRunItemEvent[]>([]);
  const [completed, setCompleted] = useState<IPayrollRunCompletedEvent | null>(null);

  const [processStarted, setProcessStarted] = useState<IPayrollProcessStarted | null>(null);
  const [processFiles, setProcessFiles] = useState<IPayrollProcessFile[]>([]);
  const [processCompleted, setProcessCompleted] = useState<IPayrollProcessCompleted | null>(null);
  const [processFailed, setProcessFailed] = useState<IPayrollProcessFailed | null>(null);
  const [signatureStarted, setSignatureStarted] =
    useState<IPayrollSignatureStarted | null>(null);
  const [signatureItems, setSignatureItems] = useState<
    IPayrollSignatureProgress[]
  >([]);
  const [signatureCompleted, setSignatureCompleted] =
    useState<IPayrollSignatureCompleted | null>(null);
  const [signatureFailed, setSignatureFailed] =
    useState<IPayrollSignatureFailed | null>(null);
  const [polledProgress, setPolledProgress] = useState<IRunProgress>();
  const isGenerationComplete = useMemo(
    () =>
      Boolean(completed) ||
      (polledProgress?.runId === runId && isProgressSnapshotComplete(polledProgress)),
    [completed, polledProgress, runId],
  );
  const { data: progressResponse } = useGetProgressQuery(runId ?? 0, {
    skip: !runId,
    pollingInterval: isGenerationComplete ? 0 : 2500,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });

  useEffect(() => {
    if (!progressResponse?.data) return;
    setPolledProgress(progressResponse.data);
  }, [progressResponse]);

  useEffect(() => {
    if (!runId || !polledProgress || polledProgress.runId !== runId) return;

    const nextProcessed = toProcessedCount(polledProgress);

    setProgress((prev) => {
      const hasAdvanced =
        nextProcessed >= prev.processed ||
        polledProgress.percent >= prev.percent ||
        polledProgress.total >= prev.total;

      return {
        runId,
        processed: Math.max(prev.processed, nextProcessed),
        total: Math.max(prev.total, polledProgress.total),
        percent: Math.max(prev.percent, polledProgress.percent),
        status: hasAdvanced ? polledProgress.status : prev.status,
      };
    });
  }, [polledProgress, runId]);

  useEffect(() => {
    if (!runId) {
      console.log("[usePayrollRunSocket] skip — no runId");
      return;
    }

    const tokens = getAuthTokensFromLocalStorage();
    if (!tokens?.accessToken) {
      console.warn("[usePayrollRunSocket] skip — no access token in localStorage");
      return;
    }

    console.log("[usePayrollRunSocket] connecting payroll namespace for runId", runId);

    setProgress({ ...INITIAL_PROGRESS, runId });
    setItems([]);
    setCompleted(null);
    setProcessStarted(null);
    setProcessFiles([]);
    setProcessCompleted(null);
    setProcessFailed(null);
    setSignatureStarted(null);
    setSignatureItems([]);
    setSignatureCompleted(null);
    setSignatureFailed(null);

    socketManager.connect(SOCKET_NAME_SPACES.PAYROLL, {
      auth: { accessToken: tokens.accessToken },
    });

    const handleProgress = (payload: IPayrollRunProgress) => {
      if (payload.runId !== runId) return;
      console.log("[usePayrollRunSocket] progress", payload);
      setProgress((prev) => ({
        runId:     payload.runId,
        processed: Math.max(prev.processed, payload.processed ?? 0),
        total:     Math.max(prev.total, payload.total ?? 0),
        percent:   Math.max(prev.percent, payload.percent ?? 0),
        status:    payload.status ?? prev.status,
      }));
    };
    const handleItem = (payload: IPayrollRunItemEvent) => {
      console.log("[usePayrollRunSocket] item", payload);
      setItems((prev) => [...prev, payload]);
    };
    const handleCompleted = (payload: IPayrollRunCompletedEvent) => {
      if (payload.runId !== runId) return;
      console.log("[usePayrollRunSocket] completed", payload);
      setProgress((prev) => ({
        ...prev,
        processed: prev.total || payload.totalEmployees,
        total:     prev.total || payload.totalEmployees,
        percent:   100,
        status:    payload.status,
      }));
      setCompleted(payload);
    };
    const handleProcessStarted = (payload: IPayrollProcessStarted) => {
      if (payload.runId !== runId) return;
      console.log("[usePayrollRunSocket] process.started", payload);
      setProcessStarted(payload);
      setProcessFailed(null);
      setProcessCompleted(null);
      setProcessFiles([]);
    };
    const handleProcessFile = (payload: IPayrollProcessFile) => {
      if (payload.runId !== runId) return;
      console.log("[usePayrollRunSocket] process.file", payload);
      setProcessFiles((prev) => {
        const idx = prev.findIndex((f) => f.fileIndex === payload.fileIndex);
        if (idx === -1) return [...prev, payload];
        const next = prev.slice();
        next[idx] = payload;
        return next;
      });
    };
    const handleProcessCompleted = (payload: IPayrollProcessCompleted) => {
      if (payload.runId !== runId) return;
      console.log("[usePayrollRunSocket] process.completed", payload);
      setProcessCompleted(payload);
    };
    const handleProcessFailed = (payload: IPayrollProcessFailed) => {
      if (payload.runId !== runId) return;
      console.error("[usePayrollRunSocket] process.failed", payload);
      setProcessFailed(payload);
    };
    const handleSignatureStarted = (payload: IPayrollSignatureStarted) => {
      if (payload.runId !== runId) return;
      console.log("[usePayrollRunSocket] signature.started", payload);
      setSignatureStarted(payload);
      setSignatureItems([]);
      setSignatureCompleted(null);
      setSignatureFailed(null);
    };
    const handleSignatureProgress = (payload: IPayrollSignatureProgress) => {
      if (payload.runId !== runId) return;
      console.log("[usePayrollRunSocket] signature.progress", payload);
      setSignatureItems((prev) => [...prev, payload]);
    };
    const handleSignatureCompleted = (payload: IPayrollSignatureCompleted) => {
      if (payload.runId !== runId) return;
      console.log("[usePayrollRunSocket] signature.completed", payload);
      setSignatureCompleted(payload);
    };
    const handleSignatureFailed = (payload: IPayrollSignatureFailed) => {
      if (payload.runId !== runId) return;
      console.error("[usePayrollRunSocket] signature.failed", payload);
      setSignatureFailed(payload);
    };
    const handleConnected = (payload: unknown) => {
      console.log("[usePayrollRunSocket] auth confirmed", payload);
    };
    const handleSocketConnect = () => {
      console.log(
        "[usePayrollRunSocket] connected — emitting subscribe for runId",
        runId,
      );
      socketManager.emit(SOCKET_NAME_SPACES.PAYROLL, SOCKET_PAYROLL_EVENTS.SUBSCRIBE, {
        runId,
      });
    };

    socketManager.on(SOCKET_NAME_SPACES.PAYROLL, "connected", handleConnected);
    socketManager.on<IPayrollRunProgress>(
      SOCKET_NAME_SPACES.PAYROLL,
      SOCKET_PAYROLL_EVENTS.RUN_PROGRESS,
      handleProgress,
    );
    socketManager.on<IPayrollRunItemEvent>(
      SOCKET_NAME_SPACES.PAYROLL,
      SOCKET_PAYROLL_EVENTS.RUN_ITEM,
      handleItem,
    );
    socketManager.on<IPayrollRunCompletedEvent>(
      SOCKET_NAME_SPACES.PAYROLL,
      SOCKET_PAYROLL_EVENTS.RUN_COMPLETED,
      handleCompleted,
    );
    socketManager.on<IPayrollProcessStarted>(
      SOCKET_NAME_SPACES.PAYROLL,
      SOCKET_PAYROLL_EVENTS.PROCESS_STARTED,
      handleProcessStarted,
    );
    socketManager.on<IPayrollProcessFile>(
      SOCKET_NAME_SPACES.PAYROLL,
      SOCKET_PAYROLL_EVENTS.PROCESS_FILE,
      handleProcessFile,
    );
    socketManager.on<IPayrollProcessCompleted>(
      SOCKET_NAME_SPACES.PAYROLL,
      SOCKET_PAYROLL_EVENTS.PROCESS_COMPLETED,
      handleProcessCompleted,
    );
    socketManager.on<IPayrollProcessFailed>(
      SOCKET_NAME_SPACES.PAYROLL,
      SOCKET_PAYROLL_EVENTS.PROCESS_FAILED,
      handleProcessFailed,
    );
    socketManager.on<IPayrollSignatureStarted>(
      SOCKET_NAME_SPACES.PAYROLL,
      SOCKET_PAYROLL_EVENTS.SIGNATURE_EMAIL_STARTED,
      handleSignatureStarted,
    );
    socketManager.on<IPayrollSignatureProgress>(
      SOCKET_NAME_SPACES.PAYROLL,
      SOCKET_PAYROLL_EVENTS.SIGNATURE_EMAIL_PROGRESS,
      handleSignatureProgress,
    );
    socketManager.on<IPayrollSignatureCompleted>(
      SOCKET_NAME_SPACES.PAYROLL,
      SOCKET_PAYROLL_EVENTS.SIGNATURE_EMAIL_COMPLETED,
      handleSignatureCompleted,
    );
    socketManager.on<IPayrollSignatureFailed>(
      SOCKET_NAME_SPACES.PAYROLL,
      SOCKET_PAYROLL_EVENTS.SIGNATURE_EMAIL_FAILED,
      handleSignatureFailed,
    );
    socketManager.onConnect(SOCKET_NAME_SPACES.PAYROLL, handleSocketConnect);

    return () => {
      socketManager.emit(SOCKET_NAME_SPACES.PAYROLL, SOCKET_PAYROLL_EVENTS.UNSUBSCRIBE, {
        runId,
      });
      socketManager.off(
        SOCKET_NAME_SPACES.PAYROLL,
        "connected",
        handleConnected,
      );
      socketManager.off(
        SOCKET_NAME_SPACES.PAYROLL,
        SOCKET_PAYROLL_EVENTS.RUN_PROGRESS,
        handleProgress,
      );
      socketManager.off(
        SOCKET_NAME_SPACES.PAYROLL,
        SOCKET_PAYROLL_EVENTS.RUN_ITEM,
        handleItem,
      );
      socketManager.off(
        SOCKET_NAME_SPACES.PAYROLL,
        SOCKET_PAYROLL_EVENTS.RUN_COMPLETED,
        handleCompleted,
      );
      socketManager.off(
        SOCKET_NAME_SPACES.PAYROLL,
        SOCKET_PAYROLL_EVENTS.PROCESS_STARTED,
        handleProcessStarted,
      );
      socketManager.off(
        SOCKET_NAME_SPACES.PAYROLL,
        SOCKET_PAYROLL_EVENTS.PROCESS_FILE,
        handleProcessFile,
      );
      socketManager.off(
        SOCKET_NAME_SPACES.PAYROLL,
        SOCKET_PAYROLL_EVENTS.PROCESS_COMPLETED,
        handleProcessCompleted,
      );
      socketManager.off(
        SOCKET_NAME_SPACES.PAYROLL,
        SOCKET_PAYROLL_EVENTS.PROCESS_FAILED,
        handleProcessFailed,
      );
      socketManager.off(
        SOCKET_NAME_SPACES.PAYROLL,
        SOCKET_PAYROLL_EVENTS.SIGNATURE_EMAIL_STARTED,
        handleSignatureStarted,
      );
      socketManager.off(
        SOCKET_NAME_SPACES.PAYROLL,
        SOCKET_PAYROLL_EVENTS.SIGNATURE_EMAIL_PROGRESS,
        handleSignatureProgress,
      );
      socketManager.off(
        SOCKET_NAME_SPACES.PAYROLL,
        SOCKET_PAYROLL_EVENTS.SIGNATURE_EMAIL_COMPLETED,
        handleSignatureCompleted,
      );
      socketManager.off(
        SOCKET_NAME_SPACES.PAYROLL,
        SOCKET_PAYROLL_EVENTS.SIGNATURE_EMAIL_FAILED,
        handleSignatureFailed,
      );
      socketManager.offConnect(SOCKET_NAME_SPACES.PAYROLL, handleSocketConnect);
      socketManager.disconnect(SOCKET_NAME_SPACES.PAYROLL);
    };
  }, [runId]);

  const isSignatureComplete = useMemo(() => {
    if (!signatureStarted) return false;
    if (signatureFailed) return true;
    if (signatureCompleted) return true;
    return false;
  }, [signatureStarted, signatureCompleted, signatureFailed]);

  return {
    progress,
    items,
    completed,
    isGenerationComplete,
    processStarted,
    processFiles,
    processCompleted,
    processFailed,
    signatureStarted,
    signatureItems,
    signatureCompleted,
    signatureFailed,
    isSignatureComplete,
  };
};
